import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { api } from "@/lib/api";
import { auth, signOut } from "@/lib/firebase";
import { Board, Column as ColumnType, CardStatus } from "@/types/board";
import { useToast } from "@/components/ui/use-toast";
import Column from "@/components/board/Column";
import { boardEvents, BOARD_UPDATED } from "@/lib/events";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Plus, Bath, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { InspirationQuote } from "@/components/InspirationQuote";



export default function BoardView() {
  const [board, setBoard] = useState<Board | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [showBoardOptions, setShowBoardOptions] = useState(false);


  useEffect(() => {
    if (boardId) {
      fetchBoard(boardId);
    }
  }, [boardId]);

  useEffect(() => {
    const handleBoardUpdate = () => {
      if (boardId) {
        fetchBoard(boardId);
      }
    };

    boardEvents.on(BOARD_UPDATED, handleBoardUpdate);
    return () => {
      boardEvents.off(BOARD_UPDATED, handleBoardUpdate);
    };
  }, [boardId]);


  const fetchBoard = async (id: string) => {
    try {
      setIsLoading(true);

      const boardData = await api.get<{ id: string; title: string; owner_id: string; created_at: string; updated_at: string }>(`/boards/${id}`);

      let rawColumns = await api.get<any[]>(`/boards/${id}/columns`);

      if (rawColumns.length === 0) {
        const defaultCols = [
          { title: "To Do",       color: "#fecaca", order: 0 },
          { title: "In Progress", color: "#fde68a", order: 1 },
          { title: "Done",        color: "#bbf7d0", order: 2 },
        ];
        rawColumns = await api.post<any[]>(`/boards/${id}/columns`, defaultCols);
      }

      const columns: ColumnType[] = rawColumns.map((col: any) => ({
        ...col,
        order: col.col_order ?? col.order ?? 0,
        cards: (col.cards || []).map((card: any) => ({
          ...card,
          order: card.card_order ?? card.order ?? 0,
          status: card.status || getCardStatus(col.title),
        })),
      }));

      setBoard({ ...boardData, columns });
    } catch (error: unknown) {
      console.error("Error fetching board:", error);
      toast({
        title: "Error",
        description: "Board not found",
        variant: "destructive",
      });
      navigate("/boards");
    } finally {
      setIsLoading(false);
    }
  };

  const getCardStatus = (columnTitle: string): CardStatus => {
    const title = columnTitle.toLowerCase();
    if (title.includes('done') || title.includes('completed')) return 'done';
    if (title.includes('progress') || title.includes('doing')) return 'in_progress';
    return 'todo';
  };


  const handleEditCard = async (_columnId: string, cardId: string, title: string, description: string) => {
    try {
      await api.patch(`/cards/${cardId}`, { title, description });

      setBoard(prev => {
        if (!prev) return null;
        return {
          ...prev,
          columns: prev.columns.map(col => ({
            ...col,
            cards: col.cards.map(card =>
              card.id === cardId ? { ...card, title, description } : card
            )
          }))
        };
      });

      toast({ title: "Success", description: "Card updated successfully" });
    } catch (error) {
      console.error("Error updating card:", error);
      toast({ title: "Error", description: "Failed to update card", variant: "destructive" });
    }
  };

  const handleDeleteCard = (_columnId: string, cardId: string) => {
    setBoard(prev => {
      if (!prev) return null;
      return {
        ...prev,
        columns: prev.columns.map(col => ({
          ...col,
          cards: col.cards.filter(card => card.id !== cardId)
        }))
      };
    });
  };

  const handleStatusChange = async (cardId: string, newStatus: string) => {
    if (!board) return;
    const targetColumn = board.columns.find(col => {
      const t = col.title.toLowerCase().trim();
      if (newStatus === 'todo')        return t === 'to do' || t === 'todo';
      if (newStatus === 'in_progress') return t === 'in progress' || t.includes('progress');
      if (newStatus === 'done')        return t === 'done' || t === 'completed';
      return false;
    });
    if (!targetColumn) return;
    try {
      await api.patch(`/cards/${cardId}`, { column_id: targetColumn.id, status: newStatus });
      boardEvents.emit(BOARD_UPDATED);
    } catch (error) {
      console.error('Error updating card status:', error);
    }
  };

  const handleEditColumn = async (columnId: string, title: string) => {
    try {
      await api.patch(`/columns/${columnId}`, { title });

      setBoard(prev => {
        if (!prev) return null;
        return {
          ...prev,
          columns: prev.columns.map(col =>
            col.id === columnId ? { ...col, title } : col
          )
        };
      });

      toast({ title: "Success", description: "Column updated successfully" });
    } catch (error) {
      console.error("Error updating column:", error);
      toast({ title: "Error", description: "Failed to update column", variant: "destructive" });
    }
  };

  const handleDeleteColumn = async (columnId: string) => {
    try {
      await api.delete(`/columns/${columnId}`);

      setBoard(prev => {
        if (!prev) return null;
        return {
          ...prev,
          columns: prev.columns.filter(col => col.id !== columnId)
        };
      });

      toast({ title: "Success", description: "Column deleted successfully" });
    } catch (error) {
      console.error("Error deleting column:", error);
      toast({ title: "Error", description: "Failed to delete column", variant: "destructive" });
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination || !board) return;

    const { source, destination, draggableId } = result;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const sourceCol = board.columns.find(col => col.id === source.droppableId);
    const destCol   = board.columns.find(col => col.id === destination.droppableId);

    if (!sourceCol || !destCol) return;

    try {
      await api.patch(`/cards/${draggableId}`, {
        column_id:  destination.droppableId,
        card_order: destination.index,
      });

      setBoard(prev => {
        if (!prev) return null;

        const movedCard = sourceCol.cards[source.index];
        if (!movedCard) return prev;

        const newColumns = prev.columns.map(col => {
          if (col.id === source.droppableId) {
            const newCards = [...col.cards];
            newCards.splice(source.index, 1);
            return { ...col, cards: newCards };
          }
          if (col.id === destination.droppableId) {
            const newCards = [...col.cards];
            newCards.splice(destination.index, 0, {
              ...movedCard,
              column_id: destination.droppableId,
              status: getCardStatus(col.title),
            });
            return { ...col, cards: newCards };
          }
          return col;
        });

        return { ...prev, columns: newColumns };
      });
    } catch (error) {
      console.error("Error updating card position:", error);
      toast({ title: "Error", description: "Failed to update card position", variant: "destructive" });
    }
  };

  const handlePriorityChange = async (cardId: string, priority: 'critical' | 'important' | 'normal') => {
    try {
      await api.patch(`/cards/${cardId}`, { priority });

      setBoard(prev => {
        if (!prev) return null;
        return {
          ...prev,
          columns: prev.columns.map(col => ({
            ...col,
            cards: col.cards.map(card =>
              card.id === cardId ? { ...card, priority } : card
            )
          }))
        };
      });

      toast({ title: "Success", description: "Card priority updated successfully" });
    } catch (error) {
      console.error("Error updating card priority:", error);
      toast({ title: "Error", description: "Failed to update card priority", variant: "destructive" });
    }
  };

  const handleAddColumn = async () => {
    try {
      if (!board) return;

      const newCol = await api.post<any>(`/boards/${board.id}/columns`, {
        title: newColumnTitle,
        color: "#4b5563",
      });

      setBoard(prev => {
        if (!prev) return null;
        return {
          ...prev,
          columns: [...prev.columns, {
            ...newCol,
            order: newCol.col_order ?? newCol.order ?? 0,
            cards: newCol.cards || [],
          }],
        };
      });

      toast({ title: "Success", description: "Column added successfully" });
      setNewColumnTitle('');
      setIsAddingColumn(false);
    } catch (error) {
      console.error("Error adding column:", error);
      toast({ title: "Error", description: "Failed to add column", variant: "destructive" });
    }
  };

  const handleBoardTitleChange = async (newTitle: string) => {
    try {
      if (!board) return;
      await api.patch(`/boards/${board.id}`, { title: newTitle });
      setBoard(prev => prev ? { ...prev, title: newTitle } : null);
      toast({ title: "Success", description: "Board title updated successfully" });
    } catch (error) {
      console.error("Error updating board title:", error);
      toast({ title: "Error", description: "Failed to update board title", variant: "destructive" });
    }
  };

  const handleDeleteBoard = async () => {
    try {
      await api.delete(`/boards/${board?.id}`);
      toast({ title: "Success", description: "Board deleted successfully" });
      navigate('/boards');
    } catch (error) {
      console.error('Error deleting board:', error);
      toast({ title: "Error", description: "Failed to delete board", variant: "destructive" });
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
      toast({ title: "Error", description: "Failed to log out", variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-pulse text-lg">Loading board...</div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg text-red-500">Board not found</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <InspirationQuote />
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Header */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/boards">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-300 hover:text-white"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to My DoDue Space
                </Button>
              </Link>
              <h1 className="text-xl font-semibold text-white">
                {board?.title || "Loading..."}
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Board
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-gray-300 hover:text-white"
              >
                <Bath className="h-4 w-4 mr-2" />
                Flush
              </Button>
            </div>
          </div>
        </div>

        {/* Board Content */}
        <div className="p-4 md:p-6">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="board" direction="horizontal" type="COLUMN">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="
                    flex flex-col md:grid
                    md:grid-cols-4
                    gap-4
                    pb-4
                    min-h-[calc(100vh-12rem)]
                    overflow-x-auto
                    md:overflow-x-visible
                  "
                >
                  {board?.columns.map((column, index) => (
                    <div key={column.id} className="w-full flex-shrink-0">
                      <Column
                        column={column}
                        index={index}
                        onEditCard={handleEditCard}
                        onDeleteCard={handleDeleteCard}
                        onEditColumn={handleEditColumn}
                        onDeleteColumn={handleDeleteColumn}
                        onPriorityChange={handlePriorityChange}
                        onStatusChange={handleStatusChange}
                      />
                    </div>
                  ))}
                  {provided.placeholder}
                  <Button
                    onClick={() => setIsAddingColumn(true)}
                    className="h-[200px] w-full border-2 border-dashed border-gray-600 hover:border-gray-400 bg-transparent text-gray-400 hover:text-gray-200 flex flex-col items-center justify-center gap-2"
                  >
                    <Plus className="h-6 w-6" />
                    Add Column
                  </Button>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        {/* Add Column Dialog */}
        <Dialog open={isAddingColumn} onOpenChange={setIsAddingColumn}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Column</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="Enter column title"
                  value={newColumnTitle}
                  onChange={(e) => setNewColumnTitle(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddingColumn(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddColumn}>Add Column</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Board Options Dialog */}
        <Dialog open={showBoardOptions} onOpenChange={setShowBoardOptions}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>DoDue Project Options</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="boardTitle">Board Title</Label>
                <Input
                  id="boardTitle"
                  placeholder="Enter board title"
                  value={board?.title || ""}
                  onChange={(e) => handleBoardTitleChange(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Button
                  variant="destructive"
                  onClick={handleDeleteBoard}
                  className="w-full"
                >
                  Delete Board
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBoardOptions(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="bg-slate-800 text-white border-gray-700">
            <DialogHeader>
              <DialogTitle>Delete Board</DialogTitle>
              <DialogDescription className="text-gray-400">
                Are you sure you want to delete "{board?.title}"? This action cannot be undone.
                All cards and columns will be permanently deleted.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex space-x-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                className="border-gray-600 text-gray-300 hover:text-white hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  handleDeleteBoard();
                  setShowDeleteConfirm(false);
                }}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Board
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
